
/**
 * Module dependencies.
 */

import { Collapse } from './collapse';
import { Form } from './forms/form';
import { IconButton } from './buttons/icon-button';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitButton } from './buttons/submit-button';
import { color, media } from 'react-components/styles';
import { formatQueryString, useQueryString } from '../query-string';
import { get, isEmpty, isNil, pick } from 'lodash';
import { useBreakpoint } from '../breakpoint';
import { useFormState } from '@seegno/react-forms';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslate } from '../i18n';
import closeIcon16 from './assets/svg/16/close.svg';
import searchIcon16 from './assets/svg/16/search.svg';
import searchIcon24 from './assets/svg/24/search.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  jsonSchema: any;
  normalizeData?: (values: any) => any;
  normalizeQuery?: (filterValues: any) => any;
  submitButtonLabel?: string;
};

/**
 * `SearchButtonProps` type.
 */

type SearchButtonProps = {
  isOpen: boolean;
  toggle: () => void;
};

/**
 * `SearchSubmitButtonProps` type.
 */

type SearchSubmitButtonProps = {
  submitButtonLabel?: string;
};

/**
 * `SearchContentProps` type.
 */

type SearchContentProps = Props & {
  isOpen: boolean;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-auto-columns: 1fr auto;
  grid-template-areas:
    '.       searchButton'
    'content content';
`;

/**
 * `Content` styled component.
 */

const Content = styled(Collapse)`
  grid-area: content;
`;

/**
 * `StyledIconButton` styled component.
 */

const StyledIconButton = styled(IconButton)`
  grid-area: searchButton;
`;

/**
 * `FormContainer` styled component.
 */

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;

  ${media.min('md')`
    flex-flow: row wrap;
    gap: 0 8px;
    margin-top: 0;

    > * {
      flex: 1 1 120px;
    }
  `}
`;

/**
 * `StyledSubmitButton` styled component.
 */

const StyledSubmitButton = styled(SubmitButton)`
  height: max-content;
  width: 100%;

  ${media.min('md')`
    flex: 0;
    width: max-content;
  `}
`;

/**
 * Clean shallow.
 */

const cleanShallow = (filters: any) => {
  return Object.keys(filters).reduce((result: any, key: string) => {
    if (isEmpty(filters[key]) || isNil(filters[key])) {
      return result;
    }

    return {
      ...result,
      [key]: filters[key]
    };
  }, {});
};

/**
 * Export `TableSearchButton` component.
 */

export function TableSearchButton(props: SearchButtonProps): JSX.Element {
  const { isOpen, toggle } = props;
  const { translate } = useTranslate();

  return (
    <StyledIconButton
      aria-label={translate(`common.actions.${isOpen ? 'close' : 'search'}`)}
      colorTheme={'gray'}
      icon={isOpen ? closeIcon16 : searchIcon16}
      iconSize={'small'}
      onClick={toggle}
      size={'small'}
    />
  );
}

/**
 * `TableSearchSubmitButton` component.
 */

function TableSearchSubmitButton({ submitButtonLabel }: SearchSubmitButtonProps): JSX.Element {
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('md', 'max');
  const { fields: { values }, meta } = useFormState();
  const { active: isActive, dirty: isDirty } = meta ?? {};
  const hasValues = useMemo(() => {
    return Object.values(values).some(value => !isNil(value));
  }, [values]);

  const isSearching = isActive && isDirty || hasValues;

  return (
    <StyledSubmitButton
      colorTheme={isSearching ? 'primary' : 'gray'}
      icon={isMobile ? searchIcon16 : searchIcon24}
      iconColor={isSearching ? color('white') : color('gray500')}
      iconSize={isMobile ? 'small' : '24px'}
      size={'large'}
    >
      {submitButtonLabel ?? translate('common.actions.search')}
    </StyledSubmitButton>
  );
}

/**
 * Export `TableSearchContent` component.
 */

export function TableSearchContent(props: SearchContentProps): JSX.Element {
  const { children, isOpen, jsonSchema, normalizeData, normalizeQuery, submitButtonLabel } = props;
  const history = useHistory();
  const location = useLocation();
  const query = useQueryString();
  const normalizedInitialValues = useMemo(() => {
    const filterValues = query?.filter ?? {};
    const values = normalizeQuery ? normalizeQuery(filterValues) : filterValues;

    return pick(values, Object.keys(jsonSchema.properties));
  }, [jsonSchema.properties, normalizeQuery, query]);

  const handleSubmit = useCallback((data: any) => {
    const filters = {
      ...get(query, 'filter', {}),
      ...normalizeData ? normalizeData(data) : data
    };

    history.push({
      ...location,
      search: formatQueryString({
        ...query,
        filter: cleanShallow(filters)
      })
    });

    return Promise.resolve();
  }, [history, location, normalizeData, query]);

  return (
    <Content isOpen={isOpen}>
      <Form
        initialValues={normalizedInitialValues}
        jsonSchema={jsonSchema}
        onSubmit={handleSubmit}
      >
        <FormContainer>
          {children}

          <TableSearchSubmitButton submitButtonLabel={submitButtonLabel} />
        </FormContainer>
      </Form>
    </Content>
  );
}

/**
 * Export `TableSearch` component.
 */

export function TableSearch(props: Props): JSX.Element {
  const { className, ...rest } = props;
  const [isOpen, setOpen] = useState<boolean>(false);
  const isMobile = useBreakpoint('md', 'max');
  const toggle = useCallback(() => setOpen(!isOpen), [isOpen]);

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  return (
    <Wrapper className={className}>
      {isMobile && (
        <TableSearchButton
          isOpen={isOpen}
          toggle={toggle}
        />
      )}

      <TableSearchContent
        {...rest}
        isOpen={isOpen}
      />
    </Wrapper>
  );
}
